import { WebAppSelection } from 'shared/projects/types.ts';
import { useContext, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { usePriceQuery } from 'src/projects/hooks/usePriceQuery.ts';
import {
    ActuatorAccessoriesPageData,
    ActuatorAccessoriesPageVariables,
} from 'src/actuators/accessories/ActuatorAccessoriesPageMapping.ts';
import { SelectionDataValue } from 'src/projects/components/SelectionDataContext.ts';
import { safeTotal } from 'src/projects/utils/SafeTotal.ts';
import { calculateTotals as calculateActuatorTotals } from 'src/actuators/checkout/CalculateTotals.ts';
import { Provide, ProviderProps } from 'src/projects/components/SelectionDataProvider.tsx';
import { FetchActuatorSelectionDedupedDocument } from 'src/projects/components/providers/ActuatorProvider.tsx';

export const RetrofitProvider = ({ selection, children }: ProviderProps & { selection: WebAppSelection }) => {
    const { userSettings, currency } = useContext(ConfigContext);
    const { data } = usePriceQuery<ActuatorAccessoriesPageData, ActuatorAccessoriesPageVariables>(
        FetchActuatorSelectionDedupedDocument,
        {
            variables: {
                locale: userSettings.currentLocale,
                label: (selection as WebAppSelection).productLabel,
                filter: { values: [] },
            },
        },
    );

    const value = useMemo<SelectionDataValue>(() => {
        if (data === undefined) {
            return { loading: true, total: null, currency: currency ?? 'USD', data: undefined };
        }
        const { total, currency: cur } = safeTotal(
            () => calculateActuatorTotals(data.accessories, userSettings, {
                actuatorLabel: selection.productLabel,
                qty: selection.qty,
                selectedAccessories: selection.selectedAccessories,
                actuatorPage: '1',
                checkoutComment: '',
                sent: false,
            }, currency),
            currency ?? 'USD',
        );
        return { loading: false, total, currency: cur, data };
    }, [data, selection, userSettings, currency]);

    return <Provide value={value}>{children}</Provide>;
};
